import React from "react";
import { ArrowLeft, Database, Zap, ChevronRight, RefreshCw, ShieldCheck, ScanFace } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { adminService } from "./adminApi";

// ── Types ─────────────────────────────────────────────────────────────────────

type FixerTool =
	| "fetch-digio-documents"
	| "check-digilocker-status"
	| "kyc-backfill"
	| "run-verifications"
	| "retrigger-selfie-match";

// ── Result box ───────────────────────────────────────────────────────────────

const ResultBox: React.FC<{ result: { ok: boolean; content: string } }> = ({ result }) => (
	<div
		className={`mt-4 rounded-lg border p-4 font-mono text-xs whitespace-pre-wrap break-all max-h-96 overflow-y-auto ${
			result.ok
				? "bg-green-50 border-green-200 text-green-900"
				: "bg-red-50 border-red-200 text-red-900"
		}`}
	>
		{result.content}
	</div>
);

// ── Sub-panel: Fetch Digio Documents ─────────────────────────────────────────

const FetchDigioDocumentsForm: React.FC<{ onBack: () => void }> = ({ onBack }) => {
	const { toast } = useToast();
	const [userId, setUserId] = React.useState("");
	const [requestId, setRequestId] = React.useState("");
	const [lenderName, setLenderName] = React.useState("");
	const [providerName, setProviderName] = React.useState("");
	const [dryRun, setDryRun] = React.useState(false);
	const [loading, setLoading] = React.useState(false);
	const [result, setResult] = React.useState<{ ok: boolean; content: string } | null>(null);

	const handleSubmit = async () => {
		setLoading(true);
		setResult(null);
		try {
			const res = await adminService.fixerFetchDigioDocuments({
				user_id: parseInt(userId, 10),
				request_id: requestId.trim() || null,
				lender_name: lenderName.trim() || null,
				provider_name: providerName.trim() || null,
				dry_run: dryRun,
			});
			const data = res.data;
			setResult({ ok: data.ok !== false, content: data.output || JSON.stringify(data, null, 2) });
			toast({ title: data.ok !== false ? "Success" : "Completed with errors" });
		} catch (error: any) {
			const errData = error.response?.data;
			setResult({ ok: false, content: errData?.error || errData?.output || JSON.stringify(errData, null, 2) || String(error) });
			toast({ title: "Request failed", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="max-w-xl w-full space-y-6">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 text-gray-500 hover:text-intellend-green" title="Back"><ArrowLeft className="h-4 w-4" /></Button>
				<div>
					<h3 className="text-lg font-bold text-gray-900">Fetch Digio Documents</h3>
					<p className="text-xs text-gray-400 font-mono">mas_digio_fetch_documents</p>
				</div>
			</div>
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-5">
				<div className="space-y-1.5">
					<Label htmlFor="fdd-user-id">User ID <span className="text-red-500">*</span></Label>
					<Input id="fdd-user-id" type="number" placeholder="e.g. 76" value={userId} onChange={(e) => setUserId(e.target.value)} min={1} className="font-mono" />
					<p className="text-xs text-gray-400">Django User primary key (required)</p>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="fdd-request-id">Request ID</Label>
					<Input id="fdd-request-id" type="text" placeholder="e.g. 83  (leave blank to auto-detect)" value={requestId} onChange={(e) => setRequestId(e.target.value)} className="font-mono" />
					<p className="text-xs text-gray-400">DigilockerKYCRequestTracker session ID — auto-resolved from DB if omitted</p>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="fdd-lender-name">Lender Name</Label>
					<Input id="fdd-lender-name" type="text" placeholder="e.g. MAS  (leave blank to auto-resolve)" value={lenderName} onChange={(e) => setLenderName(e.target.value)} />
					<p className="text-xs text-gray-400">Auto-resolved from DB when omitted</p>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="fdd-provider-name">Provider Name</Label>
					<Input id="fdd-provider-name" type="text" placeholder="e.g. PetPooja  (leave blank to auto-resolve)" value={providerName} onChange={(e) => setProviderName(e.target.value)} />
					<p className="text-xs text-gray-400">Auto-resolved from DB when omitted</p>
				</div>
				<div className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-slate-50">
					<input id="fdd-dry-run" type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="h-4 w-4 accent-intellend-green cursor-pointer" />
					<div>
						<Label htmlFor="fdd-dry-run" className="cursor-pointer font-medium">Dry Run</Label>
						<p className="text-xs text-gray-400">Fetch documents but do NOT write to DB or upload media</p>
					</div>
				</div>
				<div className="flex justify-end pt-2">
					<Button onClick={handleSubmit} disabled={loading || !userId.trim()} className="bg-intellend-green hover:bg-intellend-green/90 text-white">
						{loading ? "Running…" : "Submit"}
					</Button>
				</div>
				{result && <ResultBox result={result} />}
			</div>
		</div>
	);
};

// ── Sub-panel: Check DigiLocker Status ───────────────────────────────────────

const CheckDigilockerStatusForm: React.FC<{ onBack: () => void }> = ({ onBack }) => {
	const { toast } = useToast();
	const [requestId, setRequestId] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [result, setResult] = React.useState<{ ok: boolean; content: string } | null>(null);

	const handleSubmit = async () => {
		setLoading(true);
		setResult(null);
		try {
			const res = await adminService.fixerCheckDigilockerStatus(parseInt(requestId, 10));
			const data = res.data;
			setResult({ ok: data.ok !== false, content: JSON.stringify(data.data ?? data, null, 2) });
			toast({ title: `HTTP ${data.http_status}` });
		} catch (error: any) {
			const errData = error.response?.data;
			setResult({ ok: false, content: errData?.error || JSON.stringify(errData, null, 2) || String(error) });
			toast({ title: "Request failed", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="max-w-xl w-full space-y-6">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 text-gray-500 hover:text-intellend-green" title="Back"><ArrowLeft className="h-4 w-4" /></Button>
				<div>
					<h3 className="text-lg font-bold text-gray-900">Check DigiLocker Status</h3>
					<p className="text-xs text-gray-400 font-mono">mas_digilocker_check_status</p>
				</div>
			</div>
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-5">
				<div className="space-y-1.5">
					<Label htmlFor="cds-request-id">Request ID <span className="text-red-500">*</span></Label>
					<Input id="cds-request-id" type="number" placeholder="e.g. 49" value={requestId} onChange={(e) => setRequestId(e.target.value)} min={1} className="font-mono" />
					<p className="text-xs text-gray-400">MAS Perdix DigiLocker requestId (required)</p>
				</div>
				<div className="flex justify-end pt-2">
					<Button onClick={handleSubmit} disabled={loading || !requestId.trim()} className="bg-intellend-green hover:bg-intellend-green/90 text-white">
						{loading ? "Checking…" : "Submit"}
					</Button>
				</div>
				{result && <ResultBox result={result} />}
			</div>
		</div>
	);
};

// ── Sub-panel: KYC Backfill ───────────────────────────────────────────────────

const KycBackfillForm: React.FC<{ onBack: () => void }> = ({ onBack }) => {
	const { toast } = useToast();
	const [requestId, setRequestId] = React.useState("");
	const [userId, setUserId] = React.useState("");
	const [lenderName, setLenderName] = React.useState("MAS");
	const [providerName, setProviderName] = React.useState("PetPooja");
	const [adharFileUrl, setAdharFileUrl] = React.useState("");
	const [panFileUrl, setPanFileUrl] = React.useState("");
	const [adharXml, setAdharXml] = React.useState("");
	const [panXml, setPanXml] = React.useState("");
	const [adharPhotoBase64, setAdharPhotoBase64] = React.useState("");
	const [dryRun, setDryRun] = React.useState(false);
	const [loading, setLoading] = React.useState(false);
	const [result, setResult] = React.useState<{ ok: boolean; content: string } | null>(null);

	const handleSubmit = async () => {
		setLoading(true);
		setResult(null);
		try {
			const res = await adminService.fixerKycBackfill({
				request_id: requestId.trim(),
				user_id: parseInt(userId, 10),
				lender_name: lenderName.trim() || "MAS",
				provider_name: providerName.trim() || "PetPooja",
				adhar_file_url: adharFileUrl.trim() || undefined,
				pan_file_url: panFileUrl.trim() || undefined,
				adhar_xml: adharXml.trim() || undefined,
				pan_xml: panXml.trim() || undefined,
				adhar_photo_base64: adharPhotoBase64.trim() || undefined,
				dry_run: dryRun,
			});
			const data = res.data;
			setResult({ ok: data.ok !== false, content: data.output || JSON.stringify(data, null, 2) });
			toast({ title: data.ok !== false ? "Backfill complete" : "Completed with errors" });
		} catch (error: any) {
			const errData = error.response?.data;
			setResult({ ok: false, content: errData?.error || errData?.output || JSON.stringify(errData, null, 2) || String(error) });
			toast({ title: "Request failed", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="max-w-xl w-full space-y-6">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 text-gray-500 hover:text-intellend-green" title="Back"><ArrowLeft className="h-4 w-4" /></Button>
				<div>
					<h3 className="text-lg font-bold text-gray-900">KYC Backfill</h3>
					<p className="text-xs text-gray-400 font-mono">mas_digio_kyc_backfill</p>
				</div>
			</div>
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-5">
				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-1.5">
						<Label htmlFor="kb-request-id">Request ID <span className="text-red-500">*</span></Label>
						<Input id="kb-request-id" type="text" placeholder="e.g. 49" value={requestId} onChange={(e) => setRequestId(e.target.value)} className="font-mono" />
						<p className="text-xs text-gray-400">DigilockerKYCRequestTracker session_id</p>
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="kb-user-id">User ID <span className="text-red-500">*</span></Label>
						<Input id="kb-user-id" type="number" placeholder="e.g. 72" value={userId} onChange={(e) => setUserId(e.target.value)} min={1} className="font-mono" />
						<p className="text-xs text-gray-400">Django User primary key</p>
					</div>
				</div>
				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-1.5">
						<Label htmlFor="kb-lender-name">Lender Name</Label>
						<Input id="kb-lender-name" type="text" placeholder="MAS" value={lenderName} onChange={(e) => setLenderName(e.target.value)} />
					</div>
					<div className="space-y-1.5">
						<Label htmlFor="kb-provider-name">Provider Name</Label>
						<Input id="kb-provider-name" type="text" placeholder="PetPooja" value={providerName} onChange={(e) => setProviderName(e.target.value)} />
					</div>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="kb-adhar-url">Aadhaar File URL</Label>
					<Input id="kb-adhar-url" type="text" placeholder="https://mas.perdix.in/…" value={adharFileUrl} onChange={(e) => setAdharFileUrl(e.target.value)} className="font-mono text-xs" />
					<p className="text-xs text-gray-400">MAS Perdix URL to download Aadhaar PDF/image</p>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="kb-pan-url">PAN File URL</Label>
					<Input id="kb-pan-url" type="text" placeholder="https://mas.perdix.in/…" value={panFileUrl} onChange={(e) => setPanFileUrl(e.target.value)} className="font-mono text-xs" />
					<p className="text-xs text-gray-400">MAS Perdix URL to download PAN PDF/image</p>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="kb-adhar-xml">Aadhaar XML</Label>
					<Textarea id="kb-adhar-xml" placeholder={"<Certificate>…</Certificate>"} value={adharXml} onChange={(e) => setAdharXml(e.target.value)} className="font-mono text-xs min-h-[80px] resize-y" spellCheck={false} />
					<p className="text-xs text-gray-400">Raw Aadhaar KYC XML (Certificate &gt; CertificateData &gt; KycRes)</p>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="kb-pan-xml">PAN XML</Label>
					<Textarea id="kb-pan-xml" placeholder={"<Certificate>…</Certificate>"} value={panXml} onChange={(e) => setPanXml(e.target.value)} className="font-mono text-xs min-h-[80px] resize-y" spellCheck={false} />
					<p className="text-xs text-gray-400">Raw PAN XML (Certificate &gt; IssuedTo &gt; Person)</p>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="kb-adhar-photo">Aadhaar Photo (Base64)</Label>
					<Textarea id="kb-adhar-photo" placeholder="/9j/4AAQ…" value={adharPhotoBase64} onChange={(e) => setAdharPhotoBase64(e.target.value)} className="font-mono text-xs min-h-[60px] resize-y" spellCheck={false} />
					<p className="text-xs text-gray-400">Base64-encoded JPEG from &lt;Pht&gt; tag in dataXml</p>
				</div>
				<div className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-slate-50">
					<input id="kb-dry-run" type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="h-4 w-4 accent-intellend-green cursor-pointer" />
					<div>
						<Label htmlFor="kb-dry-run" className="cursor-pointer font-medium">Dry Run</Label>
						<p className="text-xs text-gray-400">Validate inputs without writing to the database</p>
					</div>
				</div>
				<div className="flex justify-end pt-2">
					<Button onClick={handleSubmit} disabled={loading || !requestId.trim() || !userId.trim()} className="bg-intellend-green hover:bg-intellend-green/90 text-white">
						{loading ? "Running…" : "Submit"}
					</Button>
				</div>
				{result && <ResultBox result={result} />}
			</div>
		</div>
	);
};

// ── Sub-panel: Run Verifications ─────────────────────────────────────────────

const RunVerificationsForm: React.FC<{ onBack: () => void }> = ({ onBack }) => {
	const { toast } = useToast();
	const [userId, setUserId] = React.useState("");
	const [codesRaw, setCodesRaw] = React.useState("");
	const [dryRun, setDryRun] = React.useState(true);
	const [pendingOnly, setPendingOnly] = React.useState(false);
	const [loading, setLoading] = React.useState(false);
	const [result, setResult] = React.useState<{ ok: boolean; content: string } | null>(null);

	const handleSubmit = async () => {
		setLoading(true);
		setResult(null);
		const codes = codesRaw.trim()
			? codesRaw.trim().split(/[\s,]+/).filter(Boolean)
			: null;
		try {
			const res = await adminService.fixerRunVerifications({
				user_id: parseInt(userId, 10),
				dry_run: dryRun,
				codes,
				pending_only: pendingOnly,
			});
			const data = res.data;
			setResult({ ok: data.ok !== false, content: data.output || JSON.stringify(data, null, 2) });
			toast({ title: data.ok !== false ? "Verifications complete" : "Completed with errors" });
		} catch (error: any) {
			const errData = error.response?.data;
			setResult({ ok: false, content: errData?.error || errData?.output || JSON.stringify(errData, null, 2) || String(error) });
			toast({ title: "Request failed", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="max-w-xl w-full space-y-6">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 text-gray-500 hover:text-intellend-green" title="Back"><ArrowLeft className="h-4 w-4" /></Button>
				<div>
					<h3 className="text-lg font-bold text-gray-900">Run Verifications</h3>
					<p className="text-xs text-gray-400 font-mono">run_verifications</p>
				</div>
			</div>
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-5">
				<div className="space-y-1.5">
					<Label htmlFor="rv-user-id">User ID <span className="text-red-500">*</span></Label>
					<Input id="rv-user-id" type="number" placeholder="e.g. 72" value={userId} onChange={(e) => setUserId(e.target.value)} min={1} className="font-mono" />
					<p className="text-xs text-gray-400">Django User primary key (required)</p>
				</div>

				<div className="space-y-1.5">
					<Label htmlFor="rv-codes">Verification Codes</Label>
					<Input id="rv-codes" type="text" placeholder="e.g. PAN_NAME AADHAAR_DOB  (leave blank for all)" value={codesRaw} onChange={(e) => setCodesRaw(e.target.value)} className="font-mono" />
					<p className="text-xs text-gray-400">Space or comma-separated codes — leave blank to run all</p>
				</div>

				<div className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-slate-50">
					<input id="rv-dry-run" type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} className="h-4 w-4 accent-intellend-green cursor-pointer" />
					<div>
						<Label htmlFor="rv-dry-run" className="cursor-pointer font-medium">Dry Run <span className="text-xs font-normal text-gray-400">(default: on)</span></Label>
						<p className="text-xs text-gray-400">Run all logic but do NOT save results to VerificationTracker</p>
					</div>
				</div>

				<div className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-slate-50">
					<input id="rv-pending-only" type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} className="h-4 w-4 accent-intellend-green cursor-pointer" />
					<div>
						<Label htmlFor="rv-pending-only" className="cursor-pointer font-medium">Pending Only</Label>
						<p className="text-xs text-gray-400">Only process trackers currently in 'pending' status</p>
					</div>
				</div>

				<div className="flex justify-end pt-2">
					<Button onClick={handleSubmit} disabled={loading || !userId.trim()} className="bg-intellend-green hover:bg-intellend-green/90 text-white">
						{loading ? "Running…" : "Submit"}
					</Button>
				</div>
				{result && <ResultBox result={result} />}
			</div>
		</div>
	);
};

// ── Sub-panel: Retrigger Selfie/Aadhaar Match ────────────────────────────────
// @origin: v2 | @since: 6d0c364b | @used-by: ALL

const RetriggerSelfieMatchForm: React.FC<{ onBack: () => void }> = ({ onBack }) => {
	const { toast } = useToast();
	const [userId, setUserId] = React.useState("");
	const [minimumMatch, setMinimumMatch] = React.useState("99");
	const [loading, setLoading] = React.useState(false);
	const [result, setResult] = React.useState<{ ok: boolean; content: string } | null>(null);

	const handleSubmit = async () => {
		setLoading(true);
		setResult(null);
		try {
			const res = await adminService.fixerRetriggerSelfieMatch({
				user_id: parseInt(userId, 10),
				minimum_match: minimumMatch.trim() ? parseInt(minimumMatch, 10) : undefined,
			});
			const data = res.data;
			setResult({ ok: data.ok !== false, content: data.output || JSON.stringify(data, null, 2) });
			toast({ title: data.ok !== false ? "Match complete" : "Completed with errors" });
		} catch (error: any) {
			const errData = error.response?.data;
			setResult({ ok: false, content: errData?.error || errData?.output || JSON.stringify(errData, null, 2) || String(error) });
			toast({ title: "Request failed", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="max-w-xl w-full space-y-6">
			<div className="flex items-center gap-3">
				<Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 text-gray-500 hover:text-intellend-green" title="Back"><ArrowLeft className="h-4 w-4" /></Button>
				<div>
					<h3 className="text-lg font-bold text-gray-900">Retrigger Selfie/Aadhaar Match</h3>
					<p className="text-xs text-gray-400 font-mono">retrigger_selfie_aadhaar_match</p>
				</div>
			</div>
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-5">
				<div className="space-y-1.5">
					<Label htmlFor="rsm-user-id">User ID <span className="text-red-500">*</span></Label>
					<Input id="rsm-user-id" type="number" placeholder="e.g. 42" value={userId} onChange={(e) => setUserId(e.target.value)} min={1} className="font-mono" />
					<p className="text-xs text-gray-400">Django User primary key (required)</p>
				</div>
				<div className="space-y-1.5">
					<Label htmlFor="rsm-minimum-match">Minimum Match %</Label>
					<Input id="rsm-minimum-match" type="number" placeholder="99" value={minimumMatch} onChange={(e) => setMinimumMatch(e.target.value)} min={0} max={100} className="font-mono" />
					<p className="text-xs text-gray-400">minimum_match threshold sent to Digio (default: 99)</p>
				</div>
				<div className="flex justify-end pt-2">
					<Button onClick={handleSubmit} disabled={loading || !userId.trim()} className="bg-intellend-green hover:bg-intellend-green/90 text-white">
						{loading ? "Running…" : "Submit"}
					</Button>
				</div>
				{result && <ResultBox result={result} />}
			</div>
		</div>
	);
};

// ── Main Fixer Panel ──────────────────────────────────────────────────────────

const FixerPanel: React.FC = () => {
	const [activeTool, setActiveTool] = React.useState<FixerTool | null>(null);

	if (activeTool === "fetch-digio-documents") return <FetchDigioDocumentsForm onBack={() => setActiveTool(null)} />;
	if (activeTool === "check-digilocker-status") return <CheckDigilockerStatusForm onBack={() => setActiveTool(null)} />;
	if (activeTool === "kyc-backfill") return <KycBackfillForm onBack={() => setActiveTool(null)} />;
	if (activeTool === "run-verifications") return <RunVerificationsForm onBack={() => setActiveTool(null)} />;
	if (activeTool === "retrigger-selfie-match") return <RetriggerSelfieMatchForm onBack={() => setActiveTool(null)} />;

	return (
		<div className="max-w-3xl w-full space-y-8">
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
				<h3 className="text-xl font-bold text-gray-900 mb-2">Fixer</h3>
				<p className="text-sm text-gray-500 mb-6">Data repair and fix utilities for MAS DigiLocker / Digio flows.</p>
				<div className="grid gap-3 grid-cols-1 lg:grid-cols-2">
					<Button variant="outline" className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0" onClick={() => setActiveTool("fetch-digio-documents")}>
						<div className="overflow-hidden flex items-center w-full">
							<Database className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
							<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
								<div className="font-semibold truncate">Fetch Digio Documents</div>
								<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Fetch &amp; store DigiLocker KYC docs for a user</div>
							</div>
							<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
						</div>
					</Button>

					<Button variant="outline" className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0" onClick={() => setActiveTool("check-digilocker-status")}>
						<div className="overflow-hidden flex items-center w-full">
							<Zap className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
							<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
								<div className="font-semibold truncate">Check DigiLocker Status</div>
								<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Check raw DigiLocker status for a request ID</div>
							</div>
							<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
						</div>
					</Button>

					<Button variant="outline" className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0" onClick={() => setActiveTool("kyc-backfill")}>
						<div className="overflow-hidden flex items-center w-full">
							<RefreshCw className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
							<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
								<div className="font-semibold truncate">KYC Backfill</div>
								<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Backfill MAS DigiLocker KYC data for a user</div>
							</div>
							<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
						</div>
					</Button>

					<Button variant="outline" className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0" onClick={() => setActiveTool("run-verifications")}>
						<div className="overflow-hidden flex items-center w-full">
							<ShieldCheck className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
							<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
								<div className="font-semibold truncate">Run Verifications</div>
								<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Re-run verification functions for a user</div>
							</div>
							<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
						</div>
					</Button>

					<Button variant="outline" className="justify-start gap-3 sm:gap-4 min-h-[4rem] h-auto py-3 text-base sm:text-lg border-intellend-green/20 hover:bg-intellend-green/5 hover:border-intellend-green min-w-0" onClick={() => setActiveTool("retrigger-selfie-match")}>
						<div className="overflow-hidden flex items-center w-full">
							<ScanFace className="h-5 w-5 sm:h-6 sm:w-6 text-intellend-green shrink-0" />
							<div className="text-left overflow-hidden min-w-0 flex-1 ml-3 sm:ml-4">
								<div className="font-semibold truncate">Retrigger Selfie/Aadhaar Match</div>
								<div className="text-xs sm:text-sm text-gray-500 font-normal truncate">Manually replay a face-match check for a user</div>
							</div>
							<ChevronRight className="ml-auto h-4 w-4 text-gray-400 shrink-0" />
						</div>
					</Button>
				</div>
			</div>
		</div>
	);
};

export default FixerPanel;
